export function renderedText(schema, fields) {
    const declared = {
        schema,
        prose: fields.prose ?? [],
        refs: fields.refs ?? [],
        exempt: fields.exempt ?? {},
    };
    for (const path of [...declared.prose, ...declared.refs, ...Object.keys(declared.exempt)]) {
        if (!resolves(schema, path.split('.'))) {
            throw new Error(`rendered-text: '${path}' names nothing in its schema`);
        }
    }
    return declared;
}
/**
 * Every free-text leaf of a schema, by path. Enums, literals, numbers and booleans are not free
 * text. Walks optional, nullable, default, effects, pipelines, lazies, arrays, records, unions and
 * intersections.
 */
export function freeTextLeaves(schema) {
    const found = new Map();
    const walk = (node, path, depth) => {
        if (depth > 24)
            return;
        const inner = unwrap(node);
        const kind = typeName(inner);
        if (kind === 'ZodString') {
            found.set(path, { path, kind: 'string' });
            return;
        }
        if (kind === 'ZodUnknown' || kind === 'ZodAny') {
            found.set(path, { path, kind: 'opaque' });
            return;
        }
        for (const alternative of alternatives(inner))
            walk(alternative, path, depth + 1);
        for (const [segment, child] of children(inner))
            walk(child, path === '' ? segment : `${path}.${segment}`, depth + 1);
    };
    walk(schema, '', 0);
    return [...found.values()];
}
/** Whether a declared path covers a leaf: the same path, or an ancestor of it. */
export function covers(declared, leaf) {
    return leaf === declared || leaf.startsWith(`${declared}.`);
}
const defOf = (node) => node._def;
const typeName = (node) => defOf(node).typeName;
function unwrap(node) {
    let current = node;
    for (let i = 0; i < 32; i += 1) {
        const def = defOf(current);
        switch (def.typeName) {
            case 'ZodOptional':
            case 'ZodNullable':
            case 'ZodDefault':
            case 'ZodCatch':
            case 'ZodReadonly':
                current = def.innerType;
                break;
            case 'ZodBranded':
                current = def.type;
                break;
            case 'ZodEffects':
                current = def.schema;
                break;
            case 'ZodPipeline':
                current = def.in;
                break;
            case 'ZodLazy':
                current = def.getter();
                break;
            default:
                return current;
        }
    }
    return current;
}
/** Schemas that apply at the same path: the options of a union, both sides of an intersection. */
function alternatives(node) {
    const def = defOf(node);
    if (def.typeName === 'ZodUnion' || def.typeName === 'ZodDiscriminatedUnion') {
        return [...(def.options ?? [])];
    }
    if (def.typeName === 'ZodIntersection')
        return [def.left, def.right];
    return [];
}
/** Named children: an object's fields, an array's or record's `*`. */
function children(node) {
    const def = defOf(node);
    switch (def.typeName) {
        case 'ZodObject':
            return Object.entries(node.shape);
        case 'ZodArray':
            return [['*', def.type]];
        case 'ZodRecord':
            return [['*', def.valueType]];
        case 'ZodTuple':
            return (def.items ?? []).map((item) => ['*', item]);
        default:
            return [];
    }
}
function resolves(node, segments, depth = 0) {
    if (depth > 24)
        return false;
    if (segments.length === 0)
        return true;
    const inner = unwrap(node);
    if (alternatives(inner).some((alternative) => resolves(alternative, segments, depth + 1)))
        return true;
    const kind = typeName(inner);
    // An opaque subtree holds any path at all.
    if (kind === 'ZodUnknown' || kind === 'ZodAny')
        return true;
    const [head, ...rest] = segments;
    return children(inner).some(([segment, child]) => segment === head && resolves(child, rest, depth + 1));
}
/**
 * The declared paths whose value a test refuses, in declaration order — every string at or under
 * each path, keys of objects included; an absent path yields nothing. The test is passed in, not
 * imported: contracts depends on zod alone, and the API and the courier device each hand it the
 * kernel's phone test (`@tard/kernel/phone-text`), so both refuse exactly the same text.
 */
export function renderedPathsMatching(value, declared, test) {
    const hits = [];
    for (const [kind, paths] of [['prose', declared.prose], ['refs', declared.refs]]) {
        for (const path of paths) {
            if (stringsAt(value, path.split('.')).some((text) => test(text, kind)))
                hits.push(path);
        }
    }
    return hits;
}
function stringsAt(value, segments) {
    if (value === null || value === undefined)
        return [];
    if (segments.length === 0)
        return everyString(value);
    const [head, ...rest] = segments;
    if (head === '*') {
        const items = Array.isArray(value) ? value : typeof value === 'object' ? Object.values(value) : [];
        return items.flatMap((item) => stringsAt(item, rest));
    }
    if (typeof value !== 'object' || Array.isArray(value))
        return [];
    return stringsAt(value[head], rest);
}
function everyString(value) {
    if (typeof value === 'string')
        return [value];
    if (Array.isArray(value))
        return value.flatMap(everyString);
    if (value !== null && typeof value === 'object') {
        return Object.entries(value).flatMap(([key, child]) => [key, ...everyString(child)]);
    }
    return [];
}
//# sourceMappingURL=rendered-text.js.map